<template>
  <div :class="controlClasses" class="mds-form-control mds-input-autocomplete">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :id="innerId" :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          :disabled="disabled"
          :id="uid"
          autocomplete="off"
          :placeholder="placeholder"
          type="text"
          :value="modelValue"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      :offset="0"
      :target="'#' + uid"
      :visible="optionsVisible"
      @update:visible="optionsVisible = $event"
      align="left"
      inherit-width
      no-pointer
      position="bottom"
      ref="inputAutocompletePopup"
      class="mds-input-autocomplete-popup"
    >
      <div class="mds-input-autocomplete-popup">
        <div
          class="mds-input-autocomplete-options"
          ref="inputAutocompleteOptions"
        >
          <div v-if="loading === true" class="mds-input-autocomplete-options-loading">
            <i class="fa fa-spinner" />
          </div>

          <template v-if="showEmptyText">
            <div class="mds-input-autocomplete-options-empty">
              Ничего не найдено
            </div>
          </template>

          <template v-if="optionsEmpty === false">
            <div
              :id="uid + '_option_' + index"
              :class="getOptionClasses(option)"
              :key="index"
              @click="onSelectOption(option)"
              @mouseenter="setHoveredOption(option)"
              @mouseleave="unsetHoveredOption(option)"
              class="mds-input-autocomplete-options-item"
              v-for="(option, index) of optionsLocal"
            >
              <template v-if="haveOptionSlot">
                <slot name="option" v-bind:option="option" />
              </template>
              <template v-else>
                {{ getOptionLabel(option) }}
              </template>
            </div>
          </template>
        </div>
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VInputAutocomplete.scss" />

<script lang="ts">
import debounce     from "lodash.debounce";
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";

import VPopup from "@/lib/components/VPopup/VPopup.vue";
import VIcon  from "@/lib/components/VIcon/VIcon.vue";

export default defineComponent({
  name: "VInputAutocomplete",

  components: { VPopup, VIcon },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: {
      type:    String,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: false,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => Core.constants.TEXT_ALIGNS.includes(value),
    },
    /** ID контрола в VForm @ignore */
    _controlId: {
      type:    String,
      default: null,
    },
    /** Массив опций для автодополнения */
    options: {
      type:    Array,
      default: () => [],
    },
    /** Имя свойства объекта, используемое в качестве значения */
    optionValue: {
      type:    String,
      default: "name",
    },
    /** Внешняя функция поиска. Должна возвращать массив объектов. */
    searchFunction: {
      type:    Function,
      default: null,
    },
    /** Endpoint для загрузки вариантов с сервера */
    dataUrl: {
      type:    String,
      default: null,
    },
    /** Имя параметра запроса с поисковой строкой */
    dataUrlParameterName: {
      type:    String,
      default: "pattern",
    },
    /** Минимальное количество знаков для начала поиска */
    minimumInputLength: {
      type:    Number,
      default: 0,
    },
  },

  emits: ["update:modelValue", "blur", "focus"],

  data() {
    return {
      optionsLocal:         [...this.options],
      optionsVisible:       false,
      $_searchSubscription: null,
      loading:              false,
      hoveredOption:        null,
    };
  },

  computed: {
    innerId() {
      return `inner-${this.uid}`;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.modelValue !== null
        && this.modelValue !== ''
        && this.disabled === false;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    isExternalSearch(): boolean {
      return this.searchFunction !== null || this.dataUrl !== null;
    },

    haveOptionSlot(): boolean {
      return this.$slots.option !== undefined;
    },

    optionsEmpty(): boolean {
      return this.optionsLocal.length === 0;
    },

    showEmptyText(): boolean {
      return this.optionsEmpty === true && this.loading === false;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.debounceValidation();
      }
    },
    optionsVisible(value) {
      const input = this.$el.querySelector(`#${this.uid}`);

      if (value === true) {
        const popup = this.$refs.inputAutocompletePopup;
        if (popup) {
          popup.calculatePosition();
        }
        input.addEventListener("keydown", this.keyDownListeners);
      } else {
        this.hoveredOption = null;
        this.optionsLocal  = [];
        input.removeEventListener("keydown", this.keyDownListeners);
      }
    },
  },

  methods: {
    onInput(event) {
      const value = event.target.value;
      if (this.validation.isDirty === false && this.modelValue !== value) {
        this.validation.markDirty();
      }
      this.onSearchChange(value);
      this.$emit("update:modelValue", value);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === "function") {
        this.debounceValidation.cancel();
      }
      await this.validate();
      this.$emit("blur", event.target.value);
    },

    onFocus() {
      this.validation.markTouched();
    },

    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("blur", null);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.modelValue);
    },

    // ============================================================
    // Логика автокомплита
    // ============================================================

    getOptionClasses(option) {
      return {
        hovered: this.hoveredOption !== null
          && Core.utils.isEqual(this.hoveredOption, option),
      };
    },

    keyDownListeners(event) {
      if (event.target.tagName !== "INPUT") {
        event.preventDefault();
      }

      switch (event.key) {
        case "ArrowDown":
          if (this.hoveredOption !== null) {
            const index = this.optionsLocal.findIndex((option) =>
              Core.utils.isEqual(option, this.hoveredOption)
            );
            if (index > -1 && index < this.optionsLocal.length - 1) {
              this.hoveredOption = this.optionsLocal[index + 1];
            } else if (index === this.optionsLocal.length - 1) {
              this.hoveredOption = this.optionsLocal[0];
            }
          } else {
            this.hoveredOption = this.optionsLocal[0];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "ArrowUp":
          if (this.hoveredOption !== null) {
            const index = this.optionsLocal.findIndex((option) =>
              Core.utils.isEqual(option, this.hoveredOption)
            );
            if (index > 0 && index <= this.optionsLocal.length - 1) {
              this.hoveredOption = this.optionsLocal[index - 1];
            } else if (index === 0) {
              this.hoveredOption = this.optionsLocal[this.optionsLocal.length - 1];
            }
          } else {
            this.hoveredOption = this.optionsLocal[this.optionsLocal.length - 1];
          }
          this.calculateOptionsWrapScroll(this.hoveredOption, event.key);
          break;

        case "Enter":
          if (this.hoveredOption !== null) {
            this.onSelectOption(this.hoveredOption);
          } else {
            this.optionsVisible = false;
          }
          break;

        case "Escape":
          this.optionsVisible = false;
          break;
      }
    },

    calculateOptionsWrapScroll(option, keyName) {
      const wrap        = this.$refs.inputAutocompleteOptions;
      const optionIndex = this.optionsEmpty === true
        ? -1
        : this.optionsLocal.findIndex((opt) => Core.utils.isEqual(opt, option));

      if (wrap !== null && optionIndex > -1) {
        const element = wrap.querySelector(`#${this.uid}_option_${optionIndex}`);
        if (element === null) return;

        switch (keyName) {
          case "ArrowUp":
            if (
              wrap.scrollTop > element.offsetTop ||
              element.offsetTop > wrap.scrollTop + wrap.offsetHeight
            ) {
              wrap.scrollTop = element.offsetTop;
            }
            break;
          default:
            if (
              element.offsetTop + element.offsetHeight >
              wrap.offsetHeight + wrap.scrollTop
            ) {
              wrap.scrollTop = element.offsetTop + element.offsetHeight - wrap.offsetHeight;
            } else if (element.offsetTop === 0) {
              wrap.scrollTop = 0;
            }
            break;
        }
      }
    },

    setHoveredOption(option) {
      this.hoveredOption = option;
    },

    unsetHoveredOption(option) {
      if (option !== null && this.hoveredOption !== null
        && Core.utils.isEqual(this.hoveredOption, option)) {
        this.hoveredOption = null;
      }
    },

    getOptionLabel(option) {
      return typeof option === "string" ? option : option[this.optionValue];
    },

    onSelectOption(option) {
      const value = this.getOptionLabel(option);
      this.$emit("update:modelValue", value);
      this.optionsVisible = false;
    },

    onSearchChange: debounce(function (value) {
      if (
        value === null
        || typeof value !== "string"
        || value.length < this.minimumInputLength
      ) {
        this.optionsVisible = false;
      } else {
        this.search(value);
      }
    }, 500),

    defaultSearchFunction(query) {
      const re = new RegExp(query, "i");
      return this.options.filter((option) => {
        return typeof option === "string"
          ? re.test(option)
          : re.test(option[this.optionLabel]);
      });
    },

    search(query) {
      this.loading        = true;
      this.optionsVisible = true;
      this.hoveredOption  = null;

      if (this.dataUrl !== null) {
        this.dataUrlSearch(query);
        return;
      }

      if (typeof this.searchFunction === "function") {
        this.searchFunctionSearch(query);
        return;
      }

      this.optionsLocal = this.defaultSearchFunction(query);
      this.loading      = false;
    },

    dataUrlSearch(query) {
      this.optionsLocal = [];
      const params      = {};
      params[this.dataUrlParameterName] = query;

      if (this.$_searchSubscription !== null) {
        this.$_searchSubscription.cancel();
      }

      this.$_searchSubscription = this.$http
        .rpc(this.dataUrl, params)
        .then((response) => {
          if (response && response.data && Array.isArray(response.data)) {
            this.optionsLocal = [...response.data];
          }
          this.loading = false;
        })
        .catch(() => {
          this.optionsLocal = [];
          this.loading      = false;
        });
    },

    searchFunctionSearch(query) {
      this.optionsLocal = [];
      const result      = this.searchFunction(query);

      if (result instanceof Promise) {
        result
          .then((filtered) => {
            this.optionsLocal = [...filtered];
            this.loading      = false;
          })
          .catch(() => {
            this.optionsLocal = [];
            this.loading      = false;
          });
        return;
      }

      if (Array.isArray(result)) {
        this.optionsLocal = [...result];
      } else {
        this.optionsLocal = [];
      }
      this.loading = false;
    },
  },
});
</script>
